import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import Button from 'src/components/Button';
import SelectField from 'src/components/SelectField';
import TextField from 'src/components/TextField';
import { useAppDispatch } from 'src/store';
import { addToast } from 'src/toasts/ToastsSlice';

import { ProviderFormData, providerSchema } from './adminSetup.schema';
import { useCreateProviderMutation } from './adminSetupApi';
import { describeApiError } from './describeApiError';

type Props = { onCreated: (providerId: string, label: string) => void };

const ProviderForm = ({ onCreated }: Props) => {
  const dispatch = useAppDispatch();
  const [createProvider, { isLoading }] = useCreateProviderMutation();
  const { control, handleSubmit, reset } = useForm<ProviderFormData>({
    resolver: zodResolver(providerSchema),
    mode: 'onChange',
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      department: 'Special Ed',
      position: 'Para',
      empeonId: '',
      payCode: 'Reg',
    },
  });

  const submit = handleSubmit(async (data) => {
    try {
      const res = await createProvider({
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phoneNumber: data.phoneNumber || null,
        department: data.department || null,
        position: data.position || null,
        empeonId: data.empeonId || null,
        payCode: data.payCode ?? null,
        timeZone: 'America/New_York',
      }).unwrap();
      dispatch(
        addToast({ text: `Provider created, invite sent to ${res.email}`, type: 'positive' }),
      );
      onCreated(res.providerId, `${data.firstName} ${data.lastName}`);
      reset();
    } catch (error) {
      dispatch(
        addToast({ text: describeApiError(error, 'Could not create provider'), type: 'negative' }),
      );
      console.error('Could not create provider', error);
    }
  });

  const text = (name: keyof ProviderFormData, label: string) => (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => (
        <TextField label={label} error={error?.message} {...field} value={field.value ?? ''} />
      )}
    />
  );

  return (
    <form onSubmit={submit} className="grid grid-cols-1 gap-4 md:grid-cols-3">
      {text('firstName', 'First name')}
      {text('lastName', 'Last name')}
      {text('email', 'Email')}
      {text('phoneNumber', 'Phone')}
      {text('department', 'Department (CC1)')}
      {text('position', 'Position')}
      {text('empeonId', 'Empeon ID')}
      <Controller
        name="payCode"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <SelectField
            label="Pay code"
            error={error?.message}
            value={field.value}
            onValueChange={field.onChange}
            options={[
              { label: 'Reg (W-2)', value: 'Reg' },
              { label: '1099R', value: '1099R' },
            ]}
          />
        )}
      />
      <div className="flex items-end">
        <Button type="submit" isLoading={isLoading}>
          Create provider + login
        </Button>
      </div>
    </form>
  );
};

export default ProviderForm;
